import { motion } from "framer-motion";
import { SectionLabel } from "./SectionLabel";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const stats = [
  { label: "Calories", value: "3,167 kcal" },
  { label: "Protein", value: "184 / 96 g" },
  { label: "Weight journey", value: "96.0 → 95.8 → 85.0 kg" },
];

export const EODReport = () => (
  <section className="py-24 md:py-32 lg:py-40 bg-white/50" data-testid="eod-report-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <SectionLabel number={4} title="End of Day" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <motion.h2
            {...fadeUp}
            className="font-heading text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight font-medium text-[#171717]"
          >
            Don&rsquo;t just log your day.
            <br /> Understand it.
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="mt-6 text-lg md:text-xl leading-relaxed text-[#171717]/70 max-w-[440px]"
          >
            Quantify turns everything you&rsquo;ve logged during the day into
            a simple picture of what actually happened.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-9 space-y-4"
          >
            {stats.map((s) => (
              <div
                key={s.label}
                className="flex items-baseline justify-between border-b border-[#171717]/8 pb-3"
                data-testid={`eod-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <span className="text-sm text-[#171717]/45 uppercase tracking-wide">{s.label}</span>
                <span className="font-heading text-lg font-medium text-[#171717]">{s.value}</span>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2"
        >
          <div
            className="relative mx-auto w-full max-w-[460px] rounded-3xl border border-[#171717]/8 bg-[#0B0C0E] p-7 md:p-8 text-white shadow-[0_30px_80px_-20px_rgba(23,23,23,0.18)]"
            data-testid="eod-report-card"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.18em] text-white/40">
                Daily report
              </span>
              <span className="text-xs text-white/40">Today · 17:18</span>
            </div>

            <p className="mt-6 font-heading text-2xl md:text-[1.6rem] leading-snug font-medium">
              You covered your protein early and stayed hydrated.
            </p>

            <div className="mt-7 space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/55">Protein</span>
                  <span className="font-heading font-medium">184 / 96 g</span>
                </div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full w-full rounded-full bg-[#25D366]" />
                </div>
              </div>
              <div className="flex items-center justify-between text-sm border-t border-white/8 pt-4">
                <span className="text-white/55">Calories</span>
                <span className="font-heading font-medium">3,167 kcal</span>
              </div>
            </div>

            <div className="mt-6 rounded-2xl bg-[#176B4D]/15 border border-[#25D366]/20 p-4">
              <p className="text-sm leading-relaxed text-white/80">
                With protein already well covered, your next meal can simply
                focus on hunger and overall nutrition — no need to force more.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
